import { createSlice } from '@reduxjs/toolkit'

import { normalize, schema } from 'normalizr'
import { cartTypes } from 'constants'
import { api, routes } from 'core/api'
import { createFormDataFromJSON } from 'utils/misc'

import initialState from './initialState'

function findExistingItem(prevState, productId) {
    if (!prevState) return null

    return prevState[productId] || null
}

// #### Fetch all items ######
function fetchStart(state) {
    state.error = null
    state.isFetchingCart = true
}

function fetchSuccess(state, action) {
    const { data, normalizedData, orderTotals } = action.payload
    const { items } = data

    const nextEntities = {
        items: {},
    }

    const nextIds = []

    items.forEach(item => {
        nextEntities.items[item.id] = item
        nextIds.push(item.id)
    })

    state.entities = nextEntities;
    state.result = nextIds
    
    state.checkoutAttributes = data.checkoutAttributes
    state.orderTotals = orderTotals
    
    state.misc = {
        appliedDiscounts: data.discountBox.appliedDiscountsWithCodes,
        appliedGiftcards: orderTotals.giftCards,
    }

    state.isFetchingCart = false
}

function fetchFailure(state, action) {
    const { error } = action.payload

    state.isFetchingCart = false
    state.error = error
}
// ###########################

// ####### Add item #########
function addItemSuccess(state, action) {
    const { items } = action.payload

    const nextEntities = {
        items: {},
    }

    const nextIds = []

    items.forEach(item => {
        nextEntities.items[item.id] = item
        nextIds.push(item.id)
    })
    
    state.entities = nextEntities;
    state.result = nextIds
    state.isFetchingCart = false;
}

function addItemFailure(state, action) {
    const { message } = action.payload

    state.error = message
    // Toast.error(message)
}
// ###########################

// ####### Remove item #######
function removeItemStart(state) {
    state.isRemovingItem = true
    state.error = null
}

function removeItemSuccess(state, action) {
    const { items } = action.payload

    const nextEntities = {
        items: {},
    }

    const nextIds = []

    items.forEach(item => {
        nextEntities.items[item.id] = item
        nextIds.push(item.id)
    })

    state.entities = nextEntities;
    state.result = nextIds
    state.isFetchingCart = false;

    if (!nextIds?.length) {
        state.orderTotals.subTotal = null
        state.orderTotals.subTotalDiscount = null
        state.orderTotals.orderTotal = null
    }
}

function removeItemFailure(state, action) {
    const { error } = action.payload

    state.isRemovingItem = false
    state.error = error
}
// ###########################

// ####### Update item #######
function updateItemStart(state) {
    state.error = null
}

function updateItemSuccess(state, action) {
    const { productId, quantity } = action.payload

    const prevState = { ...state.entities.items }
    const existingItem = findExistingItem(state.entities.items, productId)

    if (existingItem) {
        const nextState = {
            ...prevState,
            ...(prevState[existingItem.id].quantity = quantity),
        }

        state.entities.items = nextState
    }
}

function updateItemFailure(state, action) {
    const { error } = action.payload

    state.error = error
}
// ###########################

function discountStart(state) {
    state.isApplyingDiscount = true
    state.applyDiscountError = null
}

function discountSuccess(state, action) {
    const { data } = action.payload

    data.items.forEach(item => {
        state.entities.items[item.id] = item
    })

    state.misc.appliedDiscounts = data.appliedDiscounts

    state.orderTotals = data.orderTotals
    state.isApplyingDiscount = false
}

function discountFailure(state, action) {
    const { error } = action.payload

    state.applyDiscountError = error
    state.isApplyingDiscount = false
}

function giftCardStart(state) {
    state.isApplyingGiftCard = true
    state.applyGiftCardError = null
}

function giftCardSuccess(state, action) {
    const { data } = action.payload

    state.misc.appliedGiftCards = data.orderTotals.giftCards
    state.orderTotals = data.orderTotals
    state.isApplyingGiftCard = false
}

function giftCardFailure(state, action) {
    const { error } = action.payload

    state.applyGiftCardError = error
    state.isApplyingGiftCard = false
}

function removeDiscountSuccess(state, action) {
    const { data } = action.payload
    state.misc.appliedDiscounts = data.model.discountBox.appliedDiscountsWithCodes
    state.orderTotals = data.orderTotals
}

function removeGiftCardSuccess(state, action) {
    const { data } = action.payload

    state.error = null
    state.misc.appliedGiftCards = data.giftCards
    state.orderTotals = data
}

function updateCheckoutAttributeSuccess(state, action) {
    const { data } = action.payload

    state.orderTotals = data
}

function updateCartItemQuantity(state, action) {
    const { updatetopcartsectionhtml: quantity } = action.payload

    const newState = { ...state, quantity: quantity }

    return newState
}

function error(state, action) {
    const { error } = action.payload
    state.error = error
}

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        updateCartQuantity: updateCartItemQuantity,

        addItemToCartSuccess: addItemSuccess,
        addItemToCartFailure: addItemFailure,

        removeCartItemStart: removeItemStart,
        removeCartItemSuccess: removeItemSuccess,
        removeCartItemFailure: removeItemFailure,

        updateCartItemStart: updateItemStart,
        updateCartItemSuccess: updateItemSuccess,
        updateCartItemFailure: updateItemFailure,

        fetchCartStart: fetchStart,
        fetchCartSuccess: fetchSuccess,
        fetchCartFailure: fetchFailure,

        applyDiscountStart: discountStart,
        applyDiscountSuccess: discountSuccess,
        applyDiscountFailure: discountFailure,

        applyGiftCardStart: giftCardStart,
        applyGiftCardSuccess: giftCardSuccess,
        applyGiftCardFailure: giftCardFailure,

        removeDiscountCodeSuccess: removeDiscountSuccess,
        removeGiftCardCodeSuccess: removeGiftCardSuccess,

        checkoutAttributesUpdateSuccess: updateCheckoutAttributeSuccess,

        genericError: error,
    },
})

export const {
    addItemToCartSuccess,
    addItemToCartFailure,


    removeCartItemStart,
    removeCartItemSuccess,
    removeCartItemFailure,

    updateCartItemStart,
    updateCartItemSuccess,
    updateCartItemFailure,

    updateCartQuantity,

    fetchCartStart,
    fetchCartSuccess,
    fetchCartFailure,

    applyDiscountStart,
    applyDiscountSuccess,
    applyDiscountFailure,

    applyGiftCardStart,
    applyGiftCardSuccess,
    applyGiftCardFailure,

    removeGiftCardCodeSuccess,
    removeDiscountCodeSuccess,

    checkoutAttributesUpdateSuccess,

    genericError,
} = cartSlice.actions

const cartSchema = [new schema.Entity('items', {}, { idAttribute: 'id' })]

export function requestGetCart() {
    return async dispatch => {
        try {
            await dispatch(fetchCartStart())

            const { data, orderTotals } = await api.get(routes.cart.get)

            const normalizedData = normalize(data.items, cartSchema);

            await dispatch(
                fetchCartSuccess({
                    orderTotals,
                    data,
                    normalizedData,
                }),
            )
        } catch (error) {
            dispatch(fetchCartFailure({ error }))
        }
    }
}

export function requestAddItemToCart(product, data) {
    let form
    let detailsEndpoint = false

    // In this case there was more data to add
    // as form data, i.e. product attributes,
    // that is received as a JSON string, so we need to deserialize it
    if (data && data != "[]") {
        form = createFormDataFromJSON(data)
        detailsEndpoint = true
    }
    
    // No additional data, but we still need to send the quantity as a formdata key,
    // so before we set that, we need to have a formData object
    if (!data || data == "[]") {
        form = new FormData()
    }

    // Set quantity to either form data that was generated
    const quantityKey = `addtocart_${product.id}.EnteredQuantity`
    const quantityValue = product.quantity || 1

    form.set(quantityKey, quantityValue)

    // Get correct route
    const route = routes.cart.add(
        product.id,
        cartTypes.Cart,
        quantityValue,
        detailsEndpoint ? 'details' : 'catalog',
    )

    return async dispatch => {
        try {
            const { message, success, product, crossSells } = await api.post(
                route,
                form ?? {},
                false,
            )

            let errors = ''

            // eslint-disable-next-line no-return-assign
            Array.isArray(message) ? message.map(m => errors += m) : message

            return new Promise((resolve, reject) => {
                if (success) {
                    // Update cart
                    dispatch(requestGetCart())

                    // Show notifications 
                    resolve({ crossSells, product, message, success })
                } else {
                    // Save errors in redux
                    dispatch(addItemToCartFailure({ message: errors }))
                    
                    // Reject
                    reject(new Error(message))
                }
            })

        } catch (error) {
            console.warn('requestAddItemToCart -- failed: ', error)
        }
    }
}

export function requestDeleteItemFromCart(sciId, productId) {
    return async dispatch => {
        try {
            dispatch(removeCartItemStart())

            const { success } = await api.post(routes.cart.remove(sciId), {
                sciId,
            })

            success 
                ? await dispatch(requestGetCart())
                : await dispatch(removeCartItemFailure())

        } catch (error) {
            dispatch(removeCartItemFailure({ error }))
        }
    }
}

export function requestUpdateCartItemQuantity(productId, sciId, quantity, additive = true) {
    return async dispatch => {
        try {
            dispatch(updateCartItemStart())

            const { success, message } = await api.post(routes.cart.quantity(sciId), {
                quantity,
                isAbsoluteQuantity: !additive,
            })

            return new Promise((resolve, reject) => {
                if (success) {
                    dispatch(requestGetCart())
                    resolve({ message })
                }
                else {
                    reject({ message }); 
                    // dispatch(updateCartItemFailure({ error }))
                    // dispatch(requestGetCart())
                }
            });

            
        } catch (error) {
            console.warn('Couldnt update cart item quantity: ', error)
            // dispatch(updateCartItemFailure({ error }))
        }
    }
}

/**
 * Apply discount coupon
 * @param {string} coupon coupon code to apply
 * @returns {Request}
 */
export function requestApplyDiscountCoupon(discountCouponCode) {
    return async dispatch => {
        try {
            dispatch(applyDiscountStart())

            const { data, success, message } = await api.post(routes.cart.applyDiscount, {
                discountCouponCode,
            })

            if (!success) {
                dispatch(applyDiscountFailure({ error: message }))
                return
            }

            dispatch(applyDiscountSuccess({ data }))
        } catch (error) {
            dispatch(applyDiscountFailure({ error }))
        }
    }
}

/**
 * Apply gift card coupon
 * @param {string} coupon coupon code to apply
 * @returns {Request}
 */
export function requestApplyGiftCardCoupon(giftCardCouponCode) {
    return async dispatch => {
        try {
            dispatch(applyGiftCardStart())
            const { data, success, message } = await api.post(routes.cart.applyGiftCard, {
                giftCardCouponCode,
            })

            if (!success) {
                dispatch(applyGiftCardFailure({ error: message }))
                return
            }

            dispatch(applyGiftCardSuccess({ data }))
        } catch (error) {
            dispatch(applyGiftCardFailure({ error }))
        }
    }
}

export function requestRemoveDiscountCode(discountId) {
    return async dispatch => {
        try {
            const { data } = await api.post(routes.cart.removeDiscount, {
                discountId,
            })

            dispatch(removeDiscountCodeSuccess({ data }))
        } catch (error) {
            dispatch(error({ error }))
        }
    }
}

export function requestRemoveGiftCardCode(giftCardId) {
    return async dispatch => {
        try {
            const { data } = await api.post(routes.cart.removeGiftCard, {
                giftCardId,
            })
            dispatch(removeGiftCardCodeSuccess({ data }))
        } catch (error) {
            dispatch(error({ error }))
        }
    }
}

export function requestUpdateCheckoutAttributes(form) {
    return async dispatch => {
        try {
            const { data } = await api.post(routes.cart.updateCheckoutAttributes, form)

            dispatch(checkoutAttributesUpdateSuccess({ data }))
        } catch (error) {}
    }
}

export async function requestUploadFile(id, form) {
    const data = await api.post(routes.cart.uploadFile(id), form)

    return data
}

export default cartSlice.reducer
